/** How alerts are delivered, how often they are checked, and which ones fire. */
import { useApp } from '../../store/AppContext.jsx';
import { ALERT_TYPES } from '../../constants/defaults.js';
import Icon from '../../lib/icons.jsx';
import { beep, requestNotificationPermission } from '../../domain/alerts.js';
import { Section, Grid, Row, NumberField, CheckGrid, Toggle } from './SettingsBits.jsx';

export default function AlertsPane() {
  const { S, patchSettings, updateSettings, astate, restoreAlerts, toast } = useApp();

  const permission = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  const alertTypes = S.alertTypes || {};

  const askPermission = async () => {
    const p = await requestNotificationPermission();
    if (p === 'unsupported') { toast('This browser has no notification support'); return; }
    patchSettings({ desktopAlerts: p === 'granted' });
    toast(p === 'granted' ? 'Desktop alerts on' : 'Permission denied — check browser settings');
  };

  const sendTest = () => {
    beep(S);
    if (S.desktopAlerts && permission === 'granted') {
      try {
        // eslint-disable-next-line no-new
        new Notification('Client Master', { body: 'Test alert — notifications are working.' });
      } catch { /* blocked */ }
    }
    toast('Test alert sent');
  };

  return (
    <>
      <Section title="Delivery" hint="Alerts always show on the bell. These add a nudge on top.">
        <CheckGrid>
          <Toggle label="Desktop notifications" name="desktopAlerts" />
          <Toggle label="Sound on new alert" name="soundAlerts" />
          <Toggle label="Generate the daily brief automatically" name="autoBrief" />
        </CheckGrid>
        <Row>
          <button type="button" className="btn" onClick={askPermission}>
            <Icon name="bell" />Enable browser notifications
          </button>
          <button type="button" className="btn" onClick={sendTest}>Send a test alert</button>
        </Row>
        <p style={{ marginTop: 8 }}>Permission: <b>{permission}</b></p>
      </Section>

      <Section title="Check for changes every" hint="The ledger re-checks dates and raises anything new.">
        <Grid>
          <NumberField label="Interval" name="checkMins" unit="minutes" min={1} max={240} />
          <div className="num">
            <label>Minimum severity to alert</label>
            <select
              value={S.minSeverity || 1}
              onChange={e => patchSettings({ minSeverity: +e.target.value })}
            >
              <option value={1}>Everything</option>
              <option value={2}>Warnings and urgent</option>
              <option value={3}>Urgent only</option>
            </select>
            <small>lower severity still shows in the list</small>
          </div>
        </Grid>
      </Section>

      <Section title="Alert on" hint="Turn off anything your team does not want raised.">
        <CheckGrid>
          {ALERT_TYPES.map(([key, label]) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={alertTypes[key] !== false}
                onChange={e => updateSettings(next => {
                  next.alertTypes = { ...(next.alertTypes || {}), [key]: e.target.checked };
                })}
              />
              {label}
            </label>
          ))}
        </CheckGrid>
      </Section>

      <Section
        title="Snoozed and dismissed"
        hint={`${Object.keys(astate.snoozed || {}).length} snoozed · ${Object.keys(astate.dismissed || {}).length} dismissed.`}
      >
        <Row>
          <button type="button" className="btn" onClick={restoreAlerts}>Bring them all back</button>
        </Row>
      </Section>
    </>
  );
}
